import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; faker: any }
    | { kind: 'error'; message: string };

const LOCALES = [
    { value: 'en', label: 'English (en)' },
    { value: 'en_US', label: 'English – US (en_US)' },
    { value: 'en_GB', label: 'English – UK (en_GB)' },
    { value: 'es', label: 'Spanish (es)' },
    { value: 'fr', label: 'French (fr)' },
    { value: 'de', label: 'German (de)' },
    { value: 'it', label: 'Italian (it)' },
    { value: 'pt_BR', label: 'Portuguese – Brazil' },
    { value: 'ja', label: 'Japanese (ja)' },
    { value: 'zh_CN', label: 'Chinese – Simplified' },
    { value: 'ko', label: 'Korean (ko)' },
    { value: 'hi', label: 'Hindi (hi)' },
];

type Kind = 'first' | 'last' | 'full' | 'username';

interface State {
    locale: string;
    kind: Kind;
    count: number;
    output: string;
    lib: LibState;
    copied: boolean;
}

export default class FakeNameGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        locale: 'en',
        kind: 'full',
        count: 10,
        output: '',
        lib: { kind: 'idle' },
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private async ensureFaker(): Promise<any | null> {
        if (this.state.lib.kind === 'ready') return this.state.lib.faker;
        this.setState({ lib: { kind: 'loading' } });
        try {
            const mod = await import('@faker-js/faker');
            const localeKey = this.state.locale;
            const allLocales = mod.allLocales as Record<string, any>;
            const localeData = allLocales[localeKey] || allLocales.en;
            const faker = new mod.Faker({ locale: [localeData, allLocales.en, allLocales.base] });
            this.setState({ lib: { kind: 'ready', faker } });
            return faker;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load faker' } });
            return null;
        }
    }

    generate = async () => {
        // Reset faker if locale changed
        if (this.state.lib.kind === 'ready') {
            this.setState({ lib: { kind: 'idle' } });
        }
        const faker = await this.ensureFaker();
        if (!faker) return;
        const lines: string[] = [];
        for (let i = 0; i < this.state.count; i++) {
            let name = '';
            if (this.state.kind === 'first') name = faker.person.firstName();
            else if (this.state.kind === 'last') name = faker.person.lastName();
            else if (this.state.kind === 'full') name = faker.person.fullName();
            else name = faker.internet.username();
            lines.push(name);
        }
        this.setState({ output: lines.join('\n'), copied: false });
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { locale, kind, count, output, lib, copied } = this.state;
        const nameCount = output ? output.split('\n').filter(Boolean).length : 0;

        const form = (
            <div className={genStyles.section}>
                <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                <div className={genStyles.fieldRow}>
                    <div className={genStyles.field}>
                        <label htmlFor="fn-kind">Variant</label>
                        <select id="fn-kind" value={kind}
                            onChange={e => this.setState({ kind: e.target.value as Kind }, this.generate)}>
                            <option value="full">Full name</option>
                            <option value="first">First name only</option>
                            <option value="last">Last name only</option>
                            <option value="username">Username</option>
                        </select>
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="fn-count">Count</label>
                        <input id="fn-count" type="number" inputMode="decimal" min={1} max={500} value={count}
                            onChange={e => this.setState({ count: Math.max(1, Math.min(500, +e.target.value || 10)) }, this.generate)} />
                    </div>
                </div>
                <div className={genStyles.field}>
                    <label htmlFor="fn-locale">Locale</label>
                    <select id="fn-locale" value={locale}
                        onChange={e => this.setState({ locale: e.target.value, lib: { kind: 'idle' } }, this.generate)}>
                        {LOCALES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                </div>
                <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate}
                    disabled={lib.kind === 'loading'} style={{ alignSelf: 'flex-start' }}>
                    {lib.kind === 'loading' ? 'Loading faker…' : '↻ Regenerate'}
                </button>
            </div>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated names"
                previewMeta={`${nameCount} name${nameCount === 1 ? '' : 's'}`}
                preview={<pre className={genStyles.previewArea} aria-label="Generated names">{output}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy all'}
                    </button>
                }
                error={lib.kind === 'error' ? (lib as any).message : null}
            />
        );
    }
}
